// @flow

import toPairs from 'lodash/toPairs';
import orderBy from 'lodash/orderBy';
import {InputCommand} from '../../terminal/command';
import type {Command, Param, Commands} from '../commands/resource';
import {TriggerTree} from '../commands/triggers';
import {findCommand} from '../../executables/stripe';

type ParamTuple = [string, Param];
type CommandTuple = [string, Command];

export class StripeSuggestions {
  run(
    inputCommand: InputCommand,
    tree: Commands,
  ): Array<ParamTuple> | Array<CommandTuple> | Array<[string, {}]> {
    const empty: Array<ParamTuple> = [['', {}]];
    const args = [...inputCommand.structuredArgs];

    // if the user has typed in `stripe` with no arguments, return all
    // the commands available to it.
    if (args.length === 0) {
      const suggestions: Array<CommandTuple> = [];
      for (const [key, value] of toPairs(tree.root.commands?.stripe.commands)) {
        suggestions.push([key, value]);
      }
      return suggestions;
    }

    // `stripe trigger` has a special list of autocomplete values based
    // on the triggers themselves
    if (args.length > 0 && args[0] === 'trigger') {
      const suggestions: Array<CommandTuple> = [];

      // They've typed in a trigger, don't return suggestions
      if (args.length > 1 && inputCommand.raw.endsWith(' ')) {
        return empty;
      }

      // They haven't started typing any trigger names so return all of them
      if (args.length === 1) {
        for (const key in TriggerTree) {
          if (TriggerTree.hasOwnProperty(key)) {
            suggestions.push([key, {}]);
          }
        }

        return suggestions;
      }

      const trigger = args[1];
      for (const key in TriggerTree) {
        if (
          TriggerTree.hasOwnProperty(key) &&
          key.length >= trigger.length &&
          key.startsWith(trigger)
        ) {
          suggestions.push([key, {}]);
        }
      }
      return suggestions;
    }

    // Find the command we need to suggest for. This is either going
    // to be the full path of the command from the arguments, or the
    // path -1 as the last argument is still being typed. The
    // commandPath is useful to determine whether to render all the
    // commands or not
    const {command, commandPath} = this._findCommand(args, tree);

    // If there's still no command then something's gone wrong
    if (command === null || command === undefined) {
      return empty;
    }

    // If we've hit a command that can be run, suggest parameters,
    // otherwise suggest further commands
    if (command.run) {
      const suggestions = this.getParamSuggestions(inputCommand, command, tree);

      return orderBy(
        suggestions,
        [
          (option) =>
            option[1]?.type === 'param' && option[1].hasOwnProperty('required'),
          (option) => option[1]?.type === 'param' && !!option[1].required,
        ],
        ['desc', 'desc'],
      );
    } else {
      // If we're not at a run command and may be getting flags, return
      // empty instead of showing all the commands
      if (inputCommand.structuredFlags.length !== 0) {
        return empty;
      }

      const allPossible: Array<CommandTuple> = [];
      if (command.hasOwnProperty('commands')) {
        for (const [key, value] of toPairs(command.commands)) {
          allPossible.push([key, value]);
        }
      }

      // Get the most recent (aka current) command the user has typed
      const current = args.pop();

      // Get the name of last node found
      const last = commandPath.split('.').pop();

      // If they do not match, then filter out the commands based on
      // the input
      if (current !== last) {
        return this.matchingSubstring<Command>(current ?? '', allPossible);
      }

      // Otherwise return everything
      return allPossible;
    }
  }

  // Get suggestions for parameters. As a meta note: parameters and flags are
  // used slightly interchangeably. They're generally called flags in the command
  // line but all the flags here are representative of API parameters for the
  // requests
  getParamSuggestions(
    input: InputCommand,
    command: Command,
    tree: Commands,
  ): Array<ParamTuple> {
    const flags = [...input.structuredFlags];
    let suggestions: Array<ParamTuple> = [];
    const empty: Array<ParamTuple> = [];

    // Grab the last param. The params are sorted, in most cases the last
    // parameter will be the one that's actively being typed out. todo -- in the
    // future this should be smarter and select the incomplete param based on the
    // cursor position
    const lastParam = flags.length !== 0 ? flags[flags.length - 1] : null;

    // We only autocomplete the last param, if there is a value do not make
    // suggestions. Check if there is a space at the end of the param which will
    // denote that the user is adding another input (this knows not to check for
    // quotes)
    //
    // todo - in the future this should operate based on the cursor index.
    // todo - in the future this should check if the param name given expects a
    //        value or not
    if (lastParam && lastParam.value !== '' && !lastParam.space) {
      return empty;
    }

    // Get all the flags that have values assigned to them and are
    // therefore "completed" (ie the user is not typing the parameter names
    // anymore).
    const completedTags = new Set(
      flags.filter((flag) => flag.value !== '').map((flag) => flag.name),
    );

    // Combine all global params and all the params from the command
    // itself. This should be fine as a map/object since there should only be one
    // unique name for any given param.
    const all: {[string]: Param} = {};
    for (const [key, value] of toPairs(
      tree.root.commands?.stripe.params ?? {},
    )) {
      all[key] = value;
    }
    for (const [key, value] of toPairs(command.params ?? {})) {
      all[key] = value;
    }

    // If the user has completed typing a parameter and is about to input the
    // value, don't return any suggestions. This is a weird conditional and
    // needs to be simplified
    //
    // todo - in the future this should check if the param name given expects a
    //        value or not
    if (
      (lastParam &&
        lastParam.name in all &&
        !lastParam.space &&
        input.raw.endsWith(' ')) ||
      (lastParam &&
        lastParam.name in all &&
        !lastParam.space &&
        input.raw.endsWith('='))
    ) {
      return empty;
    }

    // There aren't any flags yet so return all of them
    if (flags.length === 0 || lastParam === null) {
      return this.dashParams(this.toTuple(all));
    }

    // Remove any parameters that are completed and cannot be duplicated
    const filteredParams = this.getFilteredParams(completedTags, all);

    // At this point in the flow, if there is a space detected at the end of a
    // parameter, the user is starting to add another to display the rest of the
    // list
    // todo - integrate `paramRequiresValue`
    if (lastParam.space) {
      return this.dashParams(this.toTuple(filteredParams));
    }

    // Find parameters that match the substring to do autocomplete suggestions
    suggestions = this.matchingSubstring<Param>(
      lastParam.name,
      this.toTuple<Param>(filteredParams),
    );

    return this.dashParams(suggestions);
  }

  // Given a partial word and a list of options, return a list of potential matches
  matchingSubstring<T>(
    partial: string,
    options: Array<[string, T]>,
  ): Array<[string, T]> {
    if (partial.trim() === '') {
      return options;
    }

    const normalized = partial.toLowerCase();

    const refinedOptions = options.filter(
      (option) =>
        option[0].length >= normalized.length &&
        option[0].slice(0, normalized.length) === normalized,
    );
    return refinedOptions;
  }

  // Removes non-repetable parameters which have been assigned.
  getFilteredParams(
    currentParams: Set<string>,
    params: {[string]: Param},
  ): {[string]: Param} {
    const refined = {};

    for (const key of Object.keys(params)) {
      const param: Param = params[key];

      if (
        param.repeatable ||
        !(
          currentParams.has(key) ||
          // $FlowIgnore[incompatible-call] not sure why this is complaining below
          (param.shorthand && currentParams.has(param.shorthand))
        )
      ) {
        refined[key] = param;
      }
    }

    return refined;
  }

  // Format parameters prior to being returned to the UI
  dashParams(params: Array<ParamTuple>): Array<ParamTuple> {
    return params.map((param) => [`--${param[0]}`, param[1]]);
  }

  // This is a slight shim/hack to minimize the rewrite needed atm
  toTuple<T>(object: {[string]: T}): Array<[string, T]> {
    const tuples: Array<[string, T]> = [];
    for (const [key, value] of toPairs(object)) {
      tuples.push([key, value]);
    }
    return tuples;
  }

  // Helper function to extract and clean up searching twice for
  // commands cleanly
  _findCommand(
    args: Array<string>,
    tree: Commands,
  ): {command: Command | null, commandPath: string} {
    const {command, commandPath} = findCommand(args, tree);

    if (command === null) {
      // This is "command -1" if still being typed
      const alt = findCommand(args.slice(0, -1), tree);

      return {
        command: alt.command,
        commandPath: alt.commandPath,
      };
    }

    return {
      command,
      commandPath,
    };
  }
}
